import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIntegrationStatus } from "@/hooks/use-integration-status";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DataTable, ColumnDefinition } from "@/components/ui/data-table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Search, 
  Users,
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  User,
  FileText,
  DollarSign,
  Calendar,
  Star,
  BarChart3,
  Truck,
  ShoppingCart,
  Globe,
  CreditCard,
  Plus,
  Zap,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";

// Types
interface CustomerContact {
  name: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

interface CustomerAddress {
  type: "billing" | "shipping";
  line1: string;
  line2?: string;
  city: string;
  county?: string;
  postcode: string;
  country: string;
}

interface CustomerPerformance {
  onTimePaymentRate: number; // Percentage
  satisfactionRating: number; // 1-5 stars
  averageOrderValue: number;
  totalOrdersPlaced: number;
  totalOrderValue: number;
  lastOrderDate: Date;
}

interface SalesOrderSummary {
  id: string;
  soNumber: string;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: string;
  totalValue: number;
  currency: string;
}

interface Customer {
  id: string;
  code: string;
  name: string;
  tradingName?: string;
  status: "active" | "inactive" | "on-hold";
  category: string;
  taxNumber?: string;
  companyRegistration?: string;
  website?: string;
  
  // Contact information
  contacts: CustomerContact[];
  addresses: CustomerAddress[];
  
  // Financial terms
  paymentTerms: string;
  creditLimit: number;
  currency: string;
  accountBalance: number; // Amount owed by customer
  
  // Performance metrics
  performance: CustomerPerformance;
  
  // Recent sales orders
  recentSalesOrders: SalesOrderSummary[];
  
  // Metadata
  syncedFrom: string;
  lastSyncDate: Date;
  createdDate: Date;
  notes?: string;
}

// Mock data
const MOCK_CUSTOMERS: Customer[] = [
  {
    id: "1",
    code: "CUST-001",
    name: "Acme Corporation Ltd",
    tradingName: "Acme Corp",
    status: "active",
    category: "Retail",
    taxNumber: "GB123456789",
    companyRegistration: "12345678",
    website: "https://www.acmecorp.com",
    contacts: [
      {
        name: "Alice Johnson",
        role: "Purchasing Manager",
        email: "alice@acmecorp.com",
        phone: "+44 20 1234 5678",
        isPrimary: true
      },
      {
        name: "Bob Smith",
        role: "Accounts Payable",
        email: "bob@acmecorp.com",
        phone: "+44 20 1234 5679",
        isPrimary: false
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "100 High Street",
        line2: "Suite 200",
        city: "London",
        county: "Greater London",
        postcode: "SW1A 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "200 Warehouse Road",
        city: "London",
        county: "Greater London",
        postcode: "SW1A 2BB",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 100000.00,
    currency: "GBP",
    accountBalance: 25750.00,
    performance: {
      onTimePaymentRate: 98.5,
      satisfactionRating: 4.8,
      averageOrderValue: 5500.00,
      totalOrdersPlaced: 64,
      totalOrderValue: 352000.00,
      lastOrderDate: new Date("2024-01-24")
    },
    recentSalesOrders: [
      {
        id: "1",
        soNumber: "SO-2024-001",
        orderDate: new Date("2024-01-20"),
        expectedDeliveryDate: new Date("2024-02-05"),
        status: "processing",
        totalValue: 15750.00,
        currency: "GBP"
      },
      {
        id: "4",
        soNumber: "SO-2024-004",
        orderDate: new Date("2024-01-15"),
        expectedDeliveryDate: new Date("2024-01-25"),
        status: "shipped",
        totalValue: 10000.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2022-03-15"),
    notes: "Premium customer. Excellent payment history and high order volume."
  },
  {
    id: "2",
    code: "CUST-002",
    name: "TechStart Solutions",
    status: "active",
    category: "Technology",
    taxNumber: "GB987654321",
    companyRegistration: "87654321",
    website: "https://www.techstart.com",
    contacts: [
      {
        name: "Charlie Davis",
        role: "Operations Director",
        email: "charlie@techstart.com",
        phone: "+44 20 9876 5432",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Tech Hub",
        line2: "Innovation Centre",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Tech Hub",
        line2: "Innovation Centre",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 50000.00,
    currency: "GBP",
    accountBalance: 12500.00,
    performance: {
      onTimePaymentRate: 85.0,
      satisfactionRating: 4.2,
      averageOrderValue: 3200.00,
      totalOrdersPlaced: 42,
      totalOrderValue: 134400.00,
      lastOrderDate: new Date("2024-01-22")
    },
    recentSalesOrders: [
      {
        id: "2",
        soNumber: "SO-2024-002",
        orderDate: new Date("2024-01-22"),
        expectedDeliveryDate: new Date("2024-02-10"),
        status: "processing",
        totalValue: 12500.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-06-20"),
    notes: "Growing account. Monitor payment terms closely."
  },
  {
    id: "3",
    code: "CUST-003",
    name: "Global Retail Group",
    tradingName: "Global Retail",
    status: "active",
    category: "Wholesale",
    taxNumber: "GB555123456",
    companyRegistration: "55512345",
    website: "https://www.globalretail.com",
    contacts: [
      {
        name: "Diana Chen",
        role: "Procurement Lead",
        email: "diana@globalretail.com",
        phone: "+44 20 5555 1234",
        isPrimary: true
      },
      {
        name: "Edward Wong",
        role: "Supply Chain Manager",
        email: "edward@globalretail.com",
        phone: "+44 20 5555 1235",
        isPrimary: false
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Retail House",
        line2: "Business Park",
        city: "Birmingham",
        county: "West Midlands",
        postcode: "B1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Distribution Centre",
        line2: "Logistics Park",
        city: "Birmingham",
        county: "West Midlands",
        postcode: "B2 2BB",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 45 days",
    creditLimit: 200000.00,
    currency: "GBP",
    accountBalance: 45600.00,
    performance: {
      onTimePaymentRate: 95.0,
      satisfactionRating: 4.5,
      averageOrderValue: 12800.00,
      totalOrdersPlaced: 38,
      totalOrderValue: 486400.00,
      lastOrderDate: new Date("2024-01-23")
    },
    recentSalesOrders: [
      {
        id: "3",
        soNumber: "SO-2024-003",
        orderDate: new Date("2024-01-23"),
        expectedDeliveryDate: new Date("2024-02-15"),
        status: "open",
        totalValue: 45600.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2022-09-10"),
    notes: "Large wholesale customer. Bulk orders with good margins."
  },
  {
    id: "4",
    code: "CUST-004",
    name: "Restaurant Supplies Co",
    status: "active",
    category: "Hospitality",
    taxNumber: "GB777888999",
    companyRegistration: "77788899",
    website: "https://www.restaurantsupplies.com",
    contacts: [
      {
        name: "Frank Martinez",
        role: "Buyer",
        email: "frank@restaurantsupplies.com",
        phone: "+44 20 7777 8888",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Hospitality House",
        city: "Leeds",
        postcode: "LS1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "456 Storage Facility",
        city: "Leeds",
        postcode: "LS1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 14 days",
    creditLimit: 30000.00,
    currency: "GBP",
    accountBalance: 8900.00,
    performance: {
      onTimePaymentRate: 92.0,
      satisfactionRating: 4.7,
      averageOrderValue: 1800.00,
      totalOrdersPlaced: 128,
      totalOrderValue: 230400.00,
      lastOrderDate: new Date("2024-01-25")
    },
    recentSalesOrders: [
      {
        id: "5",
        soNumber: "SO-2024-005",
        orderDate: new Date("2024-01-25"),
        expectedDeliveryDate: new Date("2024-02-02"),
        status: "processing",
        totalValue: 8900.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2022-01-05"),
    notes: "Frequent orders. Fast turnaround required."
  },
  {
    id: "5",
    code: "CUST-005",
    name: "BuildRight Construction",
    status: "active",
    category: "Construction",
    taxNumber: "GB444555666",
    companyRegistration: "44455566",
    website: "https://www.buildright.com",
    contacts: [
      {
        name: "Grace Taylor",
        role: "Project Manager",
        email: "grace@buildright.com",
        phone: "+44 20 4444 5555",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Construction House",
        line2: "Industrial Estate",
        city: "Bristol",
        county: "Bristol",
        postcode: "BS1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Site Office",
        line2: "Various Locations",
        city: "Bristol",
        county: "Bristol",
        postcode: "BS1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 75000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimePaymentRate: 88.0,
      satisfactionRating: 4.0,
      averageOrderValue: 8500.00,
      totalOrdersPlaced: 24,
      totalOrderValue: 204000.00,
      lastOrderDate: new Date("2024-01-18")
    },
    recentSalesOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-04-12"),
    notes: "Project-based orders. Good customer but payment can be slow."
  },
  {
    id: "6",
    code: "CUST-006",
    name: "SmallBiz Supplies",
    status: "on-hold",
    category: "Retail",
    taxNumber: "GB222333444",
    companyRegistration: "22233344",
    contacts: [
      {
        name: "Henry Wilson",
        role: "Owner",
        email: "henry@smallbizsupplies.com",
        phone: "+44 20 2222 3333",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Small Business Park",
        city: "Reading",
        postcode: "RG1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Small Business Park",
        city: "Reading",
        postcode: "RG1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 15000.00,
    currency: "GBP",
    accountBalance: 15000.00,
    performance: {
      onTimePaymentRate: 65.0,
      satisfactionRating: 3.2,
      averageOrderValue: 2100.00,
      totalOrdersPlaced: 18,
      totalOrderValue: 37800.00,
      lastOrderDate: new Date("2023-12-10")
    },
    recentSalesOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-08-20"),
    notes: "On hold - credit limit exceeded. Outstanding invoices overdue."
  },
  {
    id: "7",
    code: "CUST-007",
    name: "Office Interiors Ltd",
    status: "inactive",
    category: "Office Supplies",
    taxNumber: "GB111222333",
    companyRegistration: "11122233",
    contacts: [
      {
        name: "Iris Brown",
        role: "Procurement",
        email: "iris@officeinteriors.com",
        phone: "+44 20 1111 2222",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Office Park",
        city: "Cambridge",
        postcode: "CB1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Office Park",
        city: "Cambridge",
        postcode: "CB1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 20000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimePaymentRate: 90.0,
      satisfactionRating: 4.1,
      averageOrderValue: 3200.00,
      totalOrdersPlaced: 15,
      totalOrderValue: 48000.00,
      lastOrderDate: new Date("2023-09-15")
    },
    recentSalesOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-02-01"),
    notes: "Inactive - business closed down."
  }
];

export function CustomersPage() {
  const { navigateTo } = useNavigation();
  const integrationStatus = useIntegrationStatus();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("customers");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showQuickAddDrawer, setShowQuickAddDrawer] = useState(false);
  
  // Pagination and sorting state
  const [sortColumn, setSortColumn] = useState<keyof Customer | string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const activeCustomers = MOCK_CUSTOMERS.filter(c => c.status === "active").length;
    const totalCustomers = MOCK_CUSTOMERS.length;
    const onHoldCustomers = MOCK_CUSTOMERS.filter(c => c.status === "on-hold").length;
    const totalReceivables = MOCK_CUSTOMERS.reduce((sum, c) => sum + c.accountBalance, 0);

    return { activeCustomers, totalCustomers, onHoldCustomers, totalReceivables };
  }, []);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(MOCK_CUSTOMERS.map(c => c.category)));
    return uniqueCategories.sort();
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = MOCK_CUSTOMERS;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(customer => customer.status === statusFilter);
    }

    // Filter by category
    if (categoryFilter !== "all") {
      filtered = filtered.filter(customer => customer.category === categoryFilter);
    }

    return filtered;
  }, [searchTerm, statusFilter, categoryFilter]);

  // Sort filtered data
  const sortedData = useMemo(() => {
    const data = [...filteredData];
    
    data.sort((a, b) => {
      let aValue: any = a[sortColumn as keyof Customer];
      let bValue: any = b[sortColumn as keyof Customer];
      
      // Handle nested properties
      if (sortColumn === "onTimePaymentRate") {
        aValue = a.performance.onTimePaymentRate;
        bValue = b.performance.onTimePaymentRate;
      }
      
      // Handle date sorting
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }
      
      // Handle string sorting (case insensitive)
      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return data;
  }, [filteredData, sortColumn, sortDirection]);

  // Paginate sorted data
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter]);

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Format date and time
  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success" accent="success">Active</Badge>;
      case "inactive":
        return <Badge variant="secondary">Inactive</Badge>;
      case "on-hold":
        return <Badge variant="destructive">On Hold</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get performance badge
  const getPerformanceBadge = (rate: number) => {
    if (rate >= 95) {
      return <Badge variant="success" accent="success">Excellent</Badge>;
    } else if (rate >= 85) {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Good</Badge>;
    } else if (rate >= 75) {
      return <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">Fair</Badge>;
    } else {
      return <Badge variant="destructive">Poor</Badge>;
    }
  };

  // Render star rating
  const renderStarRating = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200"
            }`}
          />
        ))}
        <span className="text-sm text-muted-foreground ml-1">({rating.toFixed(1)})</span>
      </div>
    );
  };

  // Handle row click
  // Handle sorting
  const handleSort = (column: keyof Customer | string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRowClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDetailDrawer(true);
  };

  // Handle sync
  const handleSync = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: "Syncing with Access Financials...",
        success: "Customers synced successfully",
        error: "Failed to sync customers",
      }
    );
  };

  // Get primary contact
  const getPrimaryContact = (customer: Customer) => {
    return customer.contacts.find(c => c.isPrimary) || customer.contacts[0];
  };

  // Get billing address
  const getBillingAddress = (customer: Customer) => {
    return customer.addresses.find(a => a.type === "billing") || customer.addresses[0];
  };

  // Define table columns
  const columns: ColumnDefinition<Customer>[] = [
    {
      id: "name",
      header: "Customer",
      sortable: true,
      cell: (customer) => (
        <div>
          <div className="font-medium">{customer.name}</div>
          <div className="text-sm text-muted-foreground font-mono">{customer.code}</div>
        </div>
      ),
    },
    {
      id: "category",
      header: "Category",
      sortable: true,
      cell: (customer) => <Badge variant="outline">{customer.category}</Badge>,
    },
    {
      id: "contact",
      header: "Contact",
      cell: (customer) => {
        const primaryContact = getPrimaryContact(customer);
        return (
          <div className="text-sm">
            <div className="font-medium">{primaryContact.name}</div>
            <div className="text-muted-foreground">{primaryContact.email}</div>
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      cell: (customer) => getStatusBadge(customer.status),
    },
    {
      id: "onTimePaymentRate",
      header: "Payment Rate",
      sortable: true,
      headerClassName: "text-center",
      cellClassName: "text-center",
      cell: (customer) => (
        <div className="flex flex-col items-center gap-1">
          <div className="text-sm font-medium">
            {customer.performance.onTimePaymentRate.toFixed(1)}%
          </div>
          {getPerformanceBadge(customer.performance.onTimePaymentRate)}
        </div>
      ),
    },
    {
      id: "accountBalance",
      header: "Receivables",
      sortable: true,
      headerClassName: "text-right",
      cellClassName: "text-right font-mono font-medium",
      cell: (customer) =>
        customer.accountBalance > 0 ? (
          <span className="text-blue-600">
            {formatCurrency(customer.accountBalance, customer.currency)}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "orders",
      header: "Orders",
      headerClassName: "text-center",
      cellClassName: "text-center",
      cell: (customer) => (
        <Badge variant="outline">{customer.performance.totalOrdersPlaced}</Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (customer) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigateTo("sales-orders");
            }}
          >
            <ShoppingCart className="h-4 w-4 mr-1" />
            Orders
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
              {integrationStatus.isFinancialsIntegrated && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Synced from Access Financials
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-2">
              View and manage customer information{integrationStatus.isFinancialsIntegrated ? " synced from your ERP system" : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <HelpButton onClick={toggleHelp} />
            {/* <Button onClick={() => setShowQuickAddDrawer(true)} variant="default">
              <Plus className="h-4 w-4 mr-2" />
              Quick Add Customer
            </Button> */}
            <Button onClick={handleSync} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StaticMetricCardAlt
          title="Active Customers"
          value={stats.activeCustomers.toString()}
          icon={Users}
          variant="success"
          changeLabel={`of ${stats.totalCustomers} total`}
        />
        <StaticMetricCardAlt
          title="On Hold"
          value={stats.onHoldCustomers.toString()}
          icon={AlertTriangle}
          variant="error"
          changeLabel="Requires attention"
        />
        <StaticMetricCardAlt
          title="Total Receivables"
          value={formatCurrency(stats.totalReceivables)}
          icon={DollarSign}
          variant="info"
          changeLabel="Amount owed by customers"
        />
        <StaticMetricCardAlt
          title="Categories"
          value={categories.length.toString()}
          icon={Package}
          variant="info"
          changeLabel="Customer categories"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Customer Directory</CardTitle>
              <CardDescription>
                View customer information synced from Access Financials
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data Table */}
          <DataTable
            data={paginatedData}
            columns={columns}
            sortConfig={{ column: sortColumn, direction: sortDirection }}
            onSort={handleSort}
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={sortedData.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onRowClick={handleRowClick}
            emptyState={
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No customers found</h3>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search or filters
                </p>
              </div>
            }
            striped
          />
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={showDetailDrawer} onOpenChange={setShowDetailDrawer}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-3">
          {selectedCustomer && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SheetTitle>{selectedCustomer.name}</SheetTitle>
                    {getStatusBadge(selectedCustomer.status)}
                  </div>
                </div>
                <SheetDescription>
                  {selectedCustomer.code} • {selectedCustomer.category}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="contacts">Contacts</TabsTrigger>
                  <TabsTrigger value="performance">Performance</TabsTrigger>
                  <TabsTrigger value="orders">Orders</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  {/* Sync Information */}
                  <div className="bg-muted/50 p-3 rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Last synced:</span>
                        <span className="font-medium">{formatDateTime(selectedCustomer.lastSyncDate)}</span>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {selectedCustomer.syncedFrom}
                      </Badge>
                    </div>
                  </div>

                  {/* Basic Information */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Company Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Customer Code</Label>
                        <p className="font-medium font-mono">{selectedCustomer.code}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Legal Name</Label>
                        <p className="font-medium">{selectedCustomer.name}</p>
                      </div>
                      {selectedCustomer.tradingName && (
                        <div>
                          <Label className="text-muted-foreground">Trading Name</Label>
                          <p className="font-medium">{selectedCustomer.tradingName}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Category</Label>
                        <p className="font-medium">{selectedCustomer.category}</p>
                      </div>
                      {selectedCustomer.taxNumber && (
                        <div>
                          <Label className="text-muted-foreground">Tax Number</Label>
                          <p className="font-medium font-mono">{selectedCustomer.taxNumber}</p>
                        </div>
                      )}
                      {selectedCustomer.companyRegistration && (
                        <div>
                          <Label className="text-muted-foreground">Company Registration</Label>
                          <p className="font-medium font-mono">{selectedCustomer.companyRegistration}</p>
                        </div>
                      )}
                      {selectedCustomer.website && (
                        <div className="col-span-2">
                          <Label className="text-muted-foreground">Website</Label>
                          <a 
                            href={selectedCustomer.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-primary hover:underline flex items-center gap-1"
                          >
                            <Globe className="h-3 w-3" />
                            {selectedCustomer.website}
                          </a>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Customer Since</Label>
                        <p className="font-medium">{formatDate(selectedCustomer.createdDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Financial Terms
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Payment Terms</Label>
                        <p className="font-medium">{selectedCustomer.paymentTerms}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Currency</Label>
                        <p className="font-medium font-mono">{selectedCustomer.currency}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Credit Limit</Label>
                        <p className="font-medium text-lg">
                          {formatCurrency(selectedCustomer.creditLimit, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Current Receivables</Label>
                        <p className="font-medium text-lg text-blue-600">
                          {formatCurrency(selectedCustomer.accountBalance, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-muted-foreground">Credit Utilisation</Label>
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span>{formatCurrency(selectedCustomer.accountBalance, selectedCustomer.currency)}</span>
                            <span className="text-muted-foreground">
                              {((selectedCustomer.accountBalance / selectedCustomer.creditLimit) * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                (selectedCustomer.accountBalance / selectedCustomer.creditLimit) > 0.9 
                                  ? 'bg-destructive' 
                                  : (selectedCustomer.accountBalance / selectedCustomer.creditLimit) > 0.75
                                  ? 'bg-yellow-500'
                                  : 'bg-blue-600'
                              }`}
                              style={{ 
                                width: `${Math.min((selectedCustomer.accountBalance / selectedCustomer.creditLimit) * 100, 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Addresses
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedCustomer.addresses.map((address, idx) => (
                        <div key={idx} className="bg-muted/50 p-3 rounded-lg">
                          <div className="font-medium mb-2 capitalize">{address.type} Address</div>
                          <div className="text-sm space-y-0.5">
                            <p>{address.line1}</p>
                            {address.line2 && <p>{address.line2}</p>}
                            <p>{address.city}</p>
                            {address.county && <p>{address.county}</p>}
                            <p>{address.postcode}</p>
                            <p>{address.country}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  {selectedCustomer.notes && (
                    <div>
                      <h3 className="font-semibold mb-3">Notes</h3>
                      <p className="text-sm bg-muted p-3 rounded-md">{selectedCustomer.notes}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Contacts Tab */}
                <TabsContent value="contacts" className="space-y-4">
                  {selectedCustomer.contacts.map((contact, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg">{contact.name}</CardTitle>
                            <CardDescription>{contact.role}</CardDescription>
                          </div>
                          {contact.isPrimary && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Primary Contact
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                            {contact.phone}
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                {/* Performance Tab */}
                <TabsContent value="performance" className="space-y-6">
                  {/* Performance Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">On-Time Payment</CardTitle>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedCustomer.performance.onTimePaymentRate.toFixed(1)}%
                        </div>
                        <div className="mt-2">
                          {getPerformanceBadge(selectedCustomer.performance.onTimePaymentRate)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Satisfaction Rating</CardTitle>
                        <Star className="h-4 w-4 text-yellow-400" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedCustomer.performance.satisfactionRating.toFixed(1)}/5.0
                        </div>
                        <div className="mt-2">
                          {renderStarRating(selectedCustomer.performance.satisfactionRating)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Order Value</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {formatCurrency(selectedCustomer.performance.averageOrderValue, selectedCustomer.currency)}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          per order
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedCustomer.performance.totalOrdersPlaced}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          sales orders
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Order Value */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium">Total Order Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-600">
                        {formatCurrency(selectedCustomer.performance.totalOrderValue, selectedCustomer.currency)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Last order: {formatDate(selectedCustomer.performance.lastOrderDate)}
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Orders Tab */}
                <TabsContent value="orders" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Recent Sales Orders</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigateTo("sales-orders")}
                    >
                      View All Orders
                    </Button>
                  </div>

                  {selectedCustomer.recentSalesOrders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No recent sales orders</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedCustomer.recentSalesOrders.map((so) => (
                        <Card key={so.id}>
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-mono font-bold text-lg">{so.soNumber}</div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  Ordered: {formatDate(so.orderDate)}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Expected: {formatDate(so.expectedDeliveryDate)}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-xl mb-2">
                                  {formatCurrency(so.totalValue, so.currency)}
                                </div>
                                {getStatusBadge(so.status)}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Actions */}
              <div className="flex gap-2 pt-6 border-t mt-6">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    navigateTo("sales-orders");
                    setShowDetailDrawer(false);
                  }}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  View Sales Orders
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Quick Add Customer Drawer */}
      <Sheet open={showQuickAddDrawer} onOpenChange={setShowQuickAddDrawer}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2">
              <SheetTitle>Quick Add Customer</SheetTitle>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <Zap className="h-3 w-3 mr-1" />
                Future Implementation
              </Badge>
            </div>
            <SheetDescription>
              This will be an integrated micro frontend for quickly adding customers
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Vision Card */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  Micro Frontend Integration
                </CardTitle>
                <CardDescription>
                  This feature will be powered by a dedicated micro frontend application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Standalone Application</div>
                      <div className="text-sm text-muted-foreground">
                        Independent micro frontend that can be developed and deployed separately
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Zap className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Quick Data Entry</div>
                      <div className="text-sm text-muted-foreground">
                        Streamlined form for rapid customer onboarding with smart defaults
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <RefreshCw className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Real-time Sync</div>
                      <div className="text-sm text-muted-foreground">
                        Automatically syncs with Access Financials and updates the main customer list
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Validation & Verification</div>
                      <div className="text-sm text-muted-foreground">
                        Built-in validation for tax numbers, company registration, and duplicate detection
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <CreditCard className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Credit Limit Management</div>
                      <div className="text-sm text-muted-foreground">
                        Automatic credit assessment and limit suggestions based on business rules
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview of Future Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Form Preview</CardTitle>
                <CardDescription>
                  A glimpse of the quick add interface (non-functional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 opacity-60 pointer-events-none">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Name *</Label>
                    <Input placeholder="Enter customer name" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Trading Name</Label>
                    <Input placeholder="Optional trading name" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Code</Label>
                    <Input placeholder="Auto-generated" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tax Number</Label>
                    <Input placeholder="e.g., GB123456789" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Registration</Label>
                    <Input placeholder="e.g., 12345678" disabled />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Primary Contact Email *</Label>
                  <Input type="email" placeholder="contact@customer.com" disabled />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input placeholder="+44 20 1234 5678" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input placeholder="https://www.customer.com" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Terms</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select terms" />
                      </SelectTrigger>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Credit Limit</Label>
                    <Input placeholder="£0.00" disabled />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Billing Address</Label>
                  <Input placeholder="Street address" disabled className="mb-2" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" disabled />
                    <Input placeholder="Postcode" disabled />
                    <Input placeholder="Country" disabled />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Technical Details */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Technical Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Module Federation:</span>
                    <span className="text-muted-foreground">Webpack 5 Module Federation for runtime integration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Independent Deployment:</span>
                    <span className="text-muted-foreground">Can be updated without redeploying main app</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Shared Context:</span>
                    <span className="text-muted-foreground">Access to authentication and integration status</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Event Communication:</span>
                    <span className="text-muted-foreground">Publishes events when customers are added</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Credit Check Integration:</span>
                    <span className="text-muted-foreground">Real-time credit assessment via external APIs</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="bg-muted p-4 rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-3">
                This feature is part of the roadmap for enhanced customer management
              </p>
              <Button variant="outline" disabled>
                <Users className="h-4 w-4 mr-2" />
                Coming Soon
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />
    </div>
  );
}
